<template>
    <div class="p-5 h-full dark:bg-gray-800 dark:text-white flex">
        <div class="flex flex-col w-full justify-stretch">
            <!-- navigation header -->
            <div class="flex justify-between mb-5 shrink-0 grow-0 gap-4">
                <div class="flex gap-2 items-center">
                    <span
                        class="font-bold text-lg leading-none text-gray-500 dark:text-gray-200 mr-3 hidden lg:inline-block"
                        >{{ $t('convert_heading_start') }}</span
                    >
                    <span
                        class="font-bold text-lg leading-none tracking-wide rounded-[3px] bg-[#5560A9] text-white px-2 py-1"
                        >HEIC</span
                    >
                    <span
                        class="font-bold text-lg leading-none text-gray-500 dark:text-gray-200"
                        ><i-tabler-arrow-narrow-right
                            class="w-7 h-7"></i-tabler-arrow-narrow-right
                    ></span>
                    <format-dropdown
                        format="jpg"
                        @update="onFormatSelect"></format-dropdown>
                </div>
                <div class="flex items-center">
                    <button
                        type="button"
                        :disabled="zipDownloadGettingReady"
                        class="bg-[#ffe797] hover:bg-[#ffe07c] focus:ring-4 focus:outline-none font-medium rounded-lg text-md px-5 py-2.5 text-center inline-flex items-center gap-2 me-4 text-gray-700 hover:text-gray-900 cursor-pointer"
                        :class="{ invisible: !canDownloadAll }"
                        @click="onDownloadAllAsZipBtnClick"
                        data-tooltip-target="tooltip-download-all-zip"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-download
                            v-if="!zipDownloadGettingReady"
                            class="w-[24px] h-[24px]"></i-tabler-download>
                        <i-tabler-loader-2
                            v-if="zipDownloadGettingReady"
                            class="w-[24px] h-[24px] animate-spin"></i-tabler-loader-2>
                        ZIP
                    </button>

                    <button
                        type="button"
                        class="bg-gray-200 hover:bg-orange-200 focus:ring-4 focus:outline-none font-medium rounded-lg text-md px-5 py-2.5 text-center inline-flex items-center gap-2 me-12 text-gray-700 hover:text-gray-900 cursor-pointer"
                        :class="{ invisible: !canDownloadAll }"
                        @click="onDownloadAllBtnClick"
                        data-tooltip-target="tooltip-download-all"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-download
                            v-if="!downloadAllGettingReady"
                            class="w-[24px] h-[24px]"></i-tabler-download>
                        <i-tabler-loader-2
                            v-if="downloadAllGettingReady"
                            class="w-[24px] h-[24px] animate-spin"></i-tabler-loader-2>
                        <span class="hidden lg:inline-block">{{
                            $t('download_all_label')
                        }}</span>
                    </button>

                    <label
                        v-if="files.items.length > 0"
                        for="dropzone-file-top"
                        class="text-white bg-gradient-to-br from-pink-500 to-orange-400 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-pink-200 dark:focus:ring-pink-800 font-medium rounded-lg text-md px-5 py-2.5 text-center inline-flex items-center gap-2 cursor-pointer"
                        data-tooltip-target="tooltip-upload"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-cloud-upload
                            class="text-orange-100 h-5 w-5 tabler-stroke-1-5 shrink-0"></i-tabler-cloud-upload>
                        <span class="hidden lg:inline-block">{{
                            $t('upload_heic_files_label')
                        }}</span>
                        <input
                            ref="dropzoneFileTop"
                            id="dropzone-file-top"
                            type="file"
                            accept=".heic, image/heic"
                            multiple
                            class="hidden"
                            @change="onFileInputChange($event, 'top')" />
                    </label>
                </div>
                <div class="flex gap-3 items-center grow-0">
                    <!-- <a href="" @click.prevent="getUser()">paywall.getUser()</a> -->

                    <language-dropdown
                        class="mr-7"
                        :lang="lang"
                        @update="onLangUpdate"></language-dropdown>
                    <!-- help button -->
                    <a
                        :href="getWebsiteUrl('/how-to/')"
                        class="text-sm w-8 h-8 inline-flex justify-center items-center dark:text-[#6e7bcf]- dark:hover:text-[#8391eb] dark:text-gray-400 text-[#5560A9]- text-gray-400 hover:text-[#363f7b]"
                        @click.prevent="onHowToClick"
                        data-tooltip-target="tooltip-how-to"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-help-circle
                            class="w-8 h-8"></i-tabler-help-circle>
                    </a>
                    <!-- account button -->
                    <user-button
                        v-if="paywallEnabled"
                        :user="user.record"
                        :user-info-ready="userInfoReady"
                        @signed-in="onSignedIn"
                        @started-sign-out="onStartedSignedOut"
                        @signed-out="onSignedOut"
                        @account-clicked="onAccountClick"
                        @got-valid-promocode="onValidPromocode"></user-button>

                    <!-- <button
                        v-if="paywallEnabled"
                        type="button"
                        class="rounded-full text-sm w-8 h-8 inline-flex justify-center items-center dark:text-[#6e7bcf]- dark:text-gray-400 dark:hover:text-[#8391eb] text-[#5560A9]- text-gray-400 hover:text-[#363f7b]"
                        @click="openCustomerPortal()"
                        data-tooltip-target="tooltip-billing"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="left">
                        <i-tabler-user-circle
                            class="w-8 h-8"></i-tabler-user-circle>
                    </button> -->
                    <div class="ml-1 lg:ml-16"></div>

                    <button
                        type="button"
                        class="text-white bg-gray-400 hover:bg-gray-600 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:bg-gray-500 dark:hover:bg-gray-400 dark:hover:text-gray-900 cursor-pointer"
                        @click="closeContentPopup"
                        data-tooltip-target="tooltip-close"
                        data-tooltip-trigger="hover"
                        data-tooltip-placement="bottom">
                        <i-tabler-x class="w-6 h-6"></i-tabler-x>
                    </button>
                </div>
            </div>

            <!-- drop zone displayed by default -->
            <div
                v-if="files.items.length == 0"
                for="dropzone-file"
                ref="dropzoneElm"
                class="grow transition-all rounded-lg cursor-pointer relative flex flex-col gap-4 items-center justify-center w-full border-2 border-gray-300 border-dashed bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
                @drop="onDropZoneDrop">
                <div
                    v-if="files.items.length == 0"
                    class="flex flex-col gap-4 items-center justify-center px-5">
                    <i-tabler-cloud-upload
                        class="text-gray-500 h-10 w-10"></i-tabler-cloud-upload>
                    <p
                        class="mb-2 text-lg text-gray-500 dark:text-gray-400 text-center">
                        <!-- Click to upload HEIC files or drag and drop -->
                        {{ t('click_to_upload_message') }}
                    </p>
                </div>
                <label
                    for="dropzone-file"
                    class="absolute top-0 left-0 bottom-0 right-0 opacity-0 cursor-pointer">
                    <input
                        ref="dropzoneFile"
                        id="dropzone-file"
                        type="file"
                        accept=".heic, image/heic"
                        multiple
                        class="hidden"
                        @change="onFileInputChange($event, 'middle')" />
                </label>
            </div>
            <!-- files being converted -->
            <div class="grow overflow-auto mt-3" v-if="files.items.length > 0">
                <div class="flex flex-wrap gap-4">
                    <div
                        v-for="(file, index) in files.items"
                        :key="index"
                        class="border-[1px] border-gray-300 dark:border-gray-500 shadow-md rounded-md group/item cursor-pointer flex flex-col items-stretch justify-between"
                        @click="onItemClick($event, index)">
                        <!-- img container -->
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-center justify-center gap-2"
                            v-if="downloadItemGettingReady === index">
                            <file-processing-spinner
                                v-if="
                                    downloadItemGettingReady === index
                                "></file-processing-spinner>
                            <span class="text-sm">{{
                                $t('downloading_status_message')
                            }}</span>
                        </div>
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-center justify-center gap-2"
                            v-if="file.processing">
                            <file-processing-spinner
                                v-if="
                                    file.processing
                                "></file-processing-spinner>
                            <span class="text-sm">{{
                                $t('converting_status_message')
                            }}</span>
                        </div>
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-center justify-center gap-2"
                            v-if="
                                !file.processing &&
                                file.error &&
                                downloadItemGettingReady !== index
                            ">
                            <span
                                class="text-sm px-4 text-center"
                                v-if="file.detectedFormat"
                                >{{ $t('detected_format_message', {format:
                                `<span class="uppercase font-bold"
                                    >${file.detectedFormat}</span
                                >`}) }}
                                <span class="underline hover:no-underline">{{
                                    $t('rename_and_download_label')
                                }}</span>
                            </span>
                            <span
                                class="text-sm text-red-600 dark:text-red-400 px-4 text-center"
                                v-if="!file.detectedFormat && file.error"
                                >{{ file.error }}</span
                            >
                            <div
                                class="rounded-full w-8 h-8 bg-gray-100 right-2 top-12 flex flex-col items-center justify-center cursor-pointer"
                                @click="onDeleteBtnClick($event, index)">
                                <i-tabler-trash
                                    class="w-5 h-5 text-gray-700 hover:text-red-600"></i-tabler-trash>
                            </div>
                        </div>
                        <div
                            class="w-32 h-32 overflow-hidden rounded-t-md relative flex flex-col items-start"
                            v-if="
                                !file.processing &&
                                !file.error &&
                                downloadItemGettingReady !== index
                            ">
                            <img
                                :src="file.src"
                                class="absolute- inset-0- object-cover w-full h-full group-hover/item:opacity-50-"
                                alt="" />
                            <!-- del icon -->
                            <div
                                class="absolute rounded-full w-8 h-8 bg-white right-2 top-12 hidden group-hover/item:flex flex-col items-center justify-center cursor-pointer group/icon item-icon"
                                @click="onDeleteBtnClick($event, index)">
                                <i-tabler-trash
                                    class="w-5 h-5 text-gray-700 group-hover/icon:text-red-600"></i-tabler-trash>
                            </div>
                            <!-- share icon (REQUIRES PUBLIC URL FOR THE IMAGE, HIDE FOR NOW) -->
                            <!-- <div
                                class="absolute rounded-full w-8 h-8 bg-white right-2 top-12 hidden group-hover/item:flex flex-col items-center justify-center cursor-pointer group/icon item-icon"
                                @click="onShareBtnClick($event, index)">
                                <i-tabler-share
                                    icon="tabler:share"
                                    class="w-5 h-5 text-gray-700 group-hover/icon:text-red-600"></i-tabler-share>
                            </div> -->
                            <!-- maximize icon -->
                            <div
                                class="absolute rounded-full w-8 h-8 bg-white right-2 top-2 hidden group-hover/item:flex flex-col items-center justify-center cursor-pointer group/icon item-icon"
                                @click="onMaximizeBtnClick($event, index)">
                                <i-tabler-maximize
                                    class="w-5 h-5 text-gray-700 group-hover/icon:text-red-600"></i-tabler-maximize>
                            </div>
                            <!-- click to download message -->
                            <!-- <div
                                class="absolute bottom-0 left-0 text-sm text-center right-0 hidden group-hover/item:block bg-[#CB5FA0] text-white font-medium py-2 underline">
                                Download JPG
                            </div> -->
                        </div>
                        <!-- item name -->
                        <div
                            class="text-center text-gray-700 dark:text-gray-200 text-sm break-all p-2 w-32 font-bold group-hover/item:text-gray-300- dark:group-hover/item:text-gray-500-"
                            :title="
                                file.name !== file.shortName ? file.name : ''
                            ">
                            {{ file.shortName }}
                        </div>
                        <!-- download link -->
                        <div
                            v-if="
                                !file.processing &&
                                !file.error &&
                                downloadItemGettingReady !== index
                            "
                            class="text-sm text-center right-0 bg-[#CB5FA0] hover:bg-[#bf4c91] text-white font-medium py-2 underline rounded-b-md hover:no-underline">
                            {{
                                $t('download_label', {
                                    format: formats[file.format as FormatID]
                                        .title,
                                })
                            }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- footer -->
            <div
                class="text-sm mt-5 text-gray-500 dark:text-gray-400 text-center">
                {{ $t('support_email_label') }}
                <a
                    class="underline hover:no-underline"
                    :href="`mailto:${getSupportEmail()}`"
                    >{{ getSupportEmail() }}</a
                >
            </div>

            <!-- fullscreen drop zone: it appears only when user drags a file to the screen -->
            <div
                ref="fullscreenDropZone"
                v-if="isFullscreenDropZoneVisible"
                @dragleave="onFullscreenDropzoneDragLeave"
                class="absolute z-[999999] top-0 left-0 bottom-0 right-0 bg-gray-700/40">
                <div
                    class="absolute top-4 left-4 bottom-4 right-4 border-dashed border-4 border-white">
                    <p class="text-center text-6xl text-white font-medium mt-5">
                        {{ $t('upload_heic_files_dropzone_label') }}
                    </p>
                </div>
                <input
                    ref="dropzoneFile"
                    id="dropzone-file"
                    type="file"
                    accept=".heic, image/heic"
                    multiple
                    class="absolute top-0 left-0 bottom-0 right-0 opacity-0 cursor-pointer"
                    @change="onFileInputChange($event, 'fullscreen')" />
            </div>

            <feedback-toast
                @select-feedback="onFeedback"
                ref="feedbackToastElm"></feedback-toast>

            <!-- tooltips -->
            <tooltip-bubble id="tooltip-how-to">{{
                $t('how_to_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-billing">{{
                $t('account_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-close">{{
                $t('close_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-download-all-zip">{{
                $t('download_all_as_zip_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-download-all">{{
                $t('download_all_tooltip')
            }}</tooltip-bubble>
            <tooltip-bubble id="tooltip-upload" class="block lg:hidden">{{
                $t('upload_heic_files_label')
            }}</tooltip-bubble>

            <!-- modal -->
            <div
                id="item-modal"
                ref="modalElm"
                tabindex="-1"
                aria-hidden="true"
                class="hidden overflow-hidden fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-[calc(100%-1rem)]- bottom-0 backdrop-blur-md">
                <div
                    class="relative p-4 w-full max-w-3xl h-full overflow-hidden">
                    <!-- Modal content -->
                    <div
                        class="relative bg-white- rounded-sm shadow- dark:bg-gray-700 h-full overflow-hidden">
                        <!-- Modal header -->
                        <button
                            type="button"
                            class="fixed right-8 top-8 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center shadow cursor-pointer"
                            @click="modal.hide()">
                            <i-tabler-x class="w-6 h-6"></i-tabler-x>
                        </button>
                        <!-- Modal body -->
                        <div class="rounded-sm max-h-full overflow-auto shadow">
                            <img
                                v-if="activeItemIndex >= 0"
                                :src="files.items[activeItemIndex].src"
                                alt=""
                                class="" />
                        </div>
                        <!-- Modal footer -->
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
// import heic2any from 'heic2any';
import { Modal } from 'flowbite';
import type { ModalOptions, ModalInterface } from 'flowbite';
import type { InstanceOptions } from 'flowbite';
import { initTooltips } from 'flowbite';
import { downloadZip } from 'client-zip';
import {
    dataURItoBlob,
    getWebsiteUrl,
    isInExtension,
    openHelpPage,
    getSupportEmail,
    isDevelopment,
    filenameToFormat,
    blobToDataURL,
    getShortFileName,
    getExtensionReviewsUrl,
    getFeedbackFormUrl,
} from '~/helpers/utils';
import {
    ConvertHeicResultMessage,
    FeedbackType,
    FormatID,
    LanguageCode,
    PaywallState,
    PaywallUser,
    PaywallUserProfile,
} from '~/helpers/types';
import {
    getPaywallUser,
    getPromocode,
    setPromocode,
    validatePromocode,
} from '~/helpers/payments';
import Bugsnag, { NotifiableError } from '@bugsnag/js';
import { formats } from '~/helpers/data';
import FeedbackToast from '~/components/FeedbackToast.vue';
import { useI18n } from 'vue-i18n';
import { saveToStorage } from '~/helpers/storage';
import { detectLangOnPageLoad, setI18nLanguage } from '~/helpers/i18n';
import * as amplitude from '@amplitude/analytics-browser';

// import { H } from 'highlight.run';
// TODO: for manual light/dark switch
// import { useHead } from '@unhead/vue';

const { t } = useI18n(); // same as `useI18n({ useScope: 'global' })`

// let paywallUserData = null as any;

interface UploadedFile {
    // id is to identify the element when it's converted from HEIC to JPG
    // and the message is received from the sandbox
    id: string;
    name: string;
    shortName: string;
    // src is the src (data-uri) of the converted image
    src: string;
    processing: boolean;
    error: string;
    blob: any;
    // if another format is detected, store it here
    detectedFormat?: string;
    // output format
    format?: string;
}

const dropzoneFile = ref<HTMLInputElement | null>(null);
const dropzoneElm = ref<HTMLElement | null>(null);
const dropzoneFileTop = ref<HTMLInputElement | null>(null);
const files = reactive({
    items: [] as UploadedFile[],
});
const mouseDown = ref(false);
const modalElm = ref<HTMLElement | null>(null);
let modal: ModalInterface;
const activeItemIndex = ref(-1);
const isFullscreenDropZoneVisible = ref(false);
const zipDownloadGettingReady = ref(false);
const downloadAllGettingReady = ref(false);
// Index of the item being downloaded
const downloadItemGettingReady = ref(-1);
// For init analytics tools
const hasValidPromocode = ref(false);
const selectedFormat = ref<FormatID>('jpg');
// is opened in a new tab
const isOpenedInTab = ref(false);
const feedbackToastElm = ref<typeof FeedbackToast | null>(null);
const lang = ref<LanguageCode>('en');

// payments
const user = reactive({
    record: undefined as PaywallUser | undefined,
});
// const trialInfo = reactive({
//     data: undefined as PaywallTrialResponse | undefined,
// });
const userInfoReady = ref(false);
const paywallEnabled = computed(() => {
    return user.record?.countryMatch;
});
// const isInTrial = computed(() => {
//     return (
//         trialInfo.data?.remainingActions &&
//         Number.isInteger(trialInfo.data?.remainingActions) &&
//         trialInfo.data?.remainingActions > 0
//     );
// });

const modalOptions: ModalOptions = {
    closable: true,
    onHide: () => {
        // reset active item
        activeItemIndex.value = -1;
    },
    onShow: () => {
        // console.log('modal is shown');
    },
    onToggle: () => {
        // console.log('modal has been toggled');
    },
};

// instance options object
const instanceOptions: InstanceOptions = {
    id: 'modalEl',
    override: true,
};

//
const uniqueId = () => {
    return '_' + Math.random().toString(36).substring(2, 9);
};

const closeContentPopup = () => {
    if (isOpenedInTab.value) {
        window.close();
        return;
    }
    chrome.runtime.sendMessage({
        message: 'close_content_app',
    });
};

const onFormatSelect = (format: FormatID) => {
    amplitude.track(`Output format changed to ${format}`);
    selectedFormat.value = format;
};

// Send message to sandbox to convert HEIC image to JPG
const sendHeicImageToConvert = async (itemIndex: number, file: any) => {
    let quality = 90;
    if (isInExtension()) {
        const data = await chrome.storage.sync.get(['jpgQuality']);
        let value = parseInt(data.jpgQuality ?? 90);
        if (isNaN(value)) {
            value = 90;
        }
        quality = value;
    }

    const message = {
        message: 'convert_heic',
        itemId: files.items[itemIndex].id,
        file: file,
        format: selectedFormat.value,
        quality: quality,
    };
    if (isDevelopment()) {
        console.log('[DEV] Sent request to convert image');
    }
    const iframe = document.getElementById('sandboxFrame') as HTMLIFrameElement;
    iframe?.contentWindow?.postMessage(message, '*');
};

const handleFiles = async (uploaded: FileList) => {
    for (let i = 0; i < uploaded.length; i++) {
        const file = uploaded[i];

        // if it's HEIC, convert it to JPG
        if (/\.heic$/i.test(file.name)) {
            try {
                // const conversionResult = await heic2any({
                //     blob: file,
                //     toType: 'image/jpeg',
                //     // multiple: false,
                // });
                // const url = URL.createObjectURL(conversionResult as Blob);

                // if there is already such file name, add a number to the end
                let fileName = file.name;

                const index = files.items.findIndex(
                    (item) => item.name == fileName
                );
                if (index >= 0) {
                    let counter = 1;
                    while (
                        files.items.findIndex(
                            (item) =>
                                item.name ==
                                fileName.replace(
                                    /^(.*?)(\.heic)$/i,
                                    `$1-${counter}$2`
                                )
                        ) >= 0
                    ) {
                        counter++;
                    }
                    fileName = fileName.replace(
                        /^(.*?)(\.heic)$/i,
                        `$1-${counter}$2`
                    );
                }

                files.items.push({
                    id: uniqueId(),
                    name: fileName,
                    shortName: getShortFileName(fileName),
                    src: '',
                    processing: true,
                    error: '',
                    blob: file,
                });

                sendHeicImageToConvert(files.items.length - 1, file);
            } catch (err) {
                Bugsnag.notify(err as NotifiableError);
                if (isDevelopment()) {
                    console.log(err);
                }
            }
            continue;
        }

        // for other image formats, just add it to the list
        // (skip those for now - why do we need them at all since this is about heic?)
        // if (/\.(jpeg|jpg|png|bpm|tiff|webp)$/i.test(file.name)) {
        //     files.items.push({
        //         id: uniqueId(),
        //         name: file.name,
        //         shortName: getShortFileName(file.name),
        //         URL.createObjectURL(file);,
        //         processing: false,
        //         error: '',
        //     });
        // }
    }

    if (files.items.length > 0) {
        await nextTick();
        // init tooltip for the Upload button
        initTooltips();
    }
};

const onDropZoneDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const uploaded = e.dataTransfer?.files as FileList;
    handleFiles(uploaded);
};

const onFileInputChange = async (e: any, whichInput: string) => {
    // hide the fullscreen drop zone on any file input change
    isFullscreenDropZoneVisible.value = false;

    const uploaded =
        whichInput == 'top'
            ? (dropzoneFileTop.value?.files as FileList)
            : (dropzoneFile.value?.files as FileList);

    amplitude.track(`Uploaded ${uploaded.length} files from ${whichInput}`);

    handleFiles(uploaded);
};

// download converted image
const onItemClick = async (e: MouseEvent, index: number) => {
    amplitude.track(`Start download image #${index}`);

    downloadItemGettingReady.value = index;

    // If payment is required, returns non-false value
    const rs = await openPaywall();
    if (isDevelopment()) {
        console.log('[DEV] Download item click, openPaywall() results:', rs);
    }
    if (rs !== false) {
        downloadItemGettingReady.value = -1;
        return;
    }

    if (
        files.items[index].processing ||
        (files.items[index].error && !files.items[index].format)
    ) {
        downloadItemGettingReady.value = -1;
        return;
    }
    const dataUrl = files.items[index].src;
    const link = document.createElement('a');
    if (files.items[index].error && files.items[index].detectedFormat) {
        // If another format is detected, rename the file
        link.download = filenameToFormat(
            files.items[index].name,
            files.items[index].detectedFormat
        );
        // in this case, `dataUrl` is the original file blob
        link.href = await blobToDataURL(dataUrl);
    } else {
        const fileExtension =
            formats[files.items[index].format as FormatID].ext;
        link.download = filenameToFormat(
            files.items[index].name,
            fileExtension
        );
        link.href = dataUrl;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    downloadItemGettingReady.value = -1;
    amplitude.track(`Download successfull (#${index})`);

    showFeedbackToast();
};

const onDeleteBtnClick = (e: MouseEvent, index: number) => {
    e.stopPropagation();
    files.items.splice(index, 1);
};

// NO SHARE FOR NOW
// const onShareBtnClick = (e: MouseEvent, index: number) => {
//     e.stopPropagation();
//     //
// };

const onMaximizeBtnClick = (e: MouseEvent, index: number) => {
    e.stopPropagation();
    activeItemIndex.value = index;
    modal.show();
};

const canDownloadAll = computed(() => {
    if (files.items.length < 2) {
        return false;
    }
    let nonErrorFiles = 0;
    for (let i = 0; i < files.items.length; i++) {
        if (files.items[i].processing) {
            return false;
        }
        if (!files.items[i].error) {
            nonErrorFiles++;
        }
    }
    if (nonErrorFiles < 2) {
        return false;
    }
    return true;
});

let downloadAllIndex = 0;
let downloadingAll = false;
const onDownloadAllBtnClick = async () => {
    if (downloadingAll) {
        return;
    }

    amplitude.track(`Start download all`);

    downloadAllGettingReady.value = true;

    const rs = await openPaywall();
    if (rs !== false) {
        downloadAllGettingReady.value = false;
        return;
    }

    downloadingAll = true;
    downloadAllIndex = 0;

    downloadAllGettingReady.value = false;
    downloadAllFiles();
    showFeedbackToast();
};

// Based on: https://stackoverflow.com/a/56245610
let dlAllAuxLink: HTMLAnchorElement = document.createElement('a');
document.body.appendChild(dlAllAuxLink);

const downloadAllFiles = () => {
    amplitude.track(`Download all: start successfull`);

    const index = downloadAllIndex;

    // End of the list - exit
    if (index > files.items.length - 1) {
        downloadingAll = false;
        return;
    }

    downloadAllIndex++;

    // If the file is processing or has an error, skip it
    if (
        files.items[index].processing ||
        (files.items[index].error && !files.items[index].format)
    ) {
        downloadAllFiles();
        return;
    }

    setTimeout(async function () {
        const dataUrl = files.items[index].src;
        if (files.items[index].error && files.items[index].format) {
            // If another format is detected, rename the file
            dlAllAuxLink.download = filenameToFormat(
                files.items[index].name,
                files.items[index].format as string
            );
            // in this case, `dataUrl` is the original file blob
            dlAllAuxLink.href = await blobToDataURL(dataUrl);
        } else {
            const fileExtension =
                formats[files.items[index].format as FormatID].ext;
            dlAllAuxLink.download = filenameToFormat(
                files.items[index].name,
                fileExtension
            );
            dlAllAuxLink.href = dataUrl;
        }
        dlAllAuxLink.click();

        if (index < files.items.length - 1) {
            downloadAllFiles();
        } else {
            downloadingAll = false;
        }
    }, 200); // if less than 200, not all files are downloaded in Firefox
};

// download all as a zip file
const onDownloadAllAsZipBtnClick = async () => {
    amplitude.track(`Start download zip`);

    zipDownloadGettingReady.value = true;
    const rs = await openPaywall();
    if (rs !== false) {
        zipDownloadGettingReady.value = false;
        return;
    }

    const zipItems = [];
    for (let i = 0; i < files.items.length; i++) {
        const file = files.items[i];
        if (file.error) {
            continue;
        }
        const fileExtension = formats[files.items[i].format as FormatID].ext;
        const fileName = filenameToFormat(files.items[i].name, fileExtension);

        zipItems.push({
            name: fileName,
            input: dataURItoBlob(file.src),
        });
    }

    // get the ZIP stream in a Blob
    let blob;
    try {
        blob = await downloadZip(zipItems).blob();
    } catch (error) {
        zipDownloadGettingReady.value = false;
        Bugsnag.notify(error as NotifiableError);
        console.error(error);
        return;
    }

    zipDownloadGettingReady.value = false;

    // convert date to yyyy-mm-dd-timestamp format
    const date = new Date();
    const formattedDate =
        date.toISOString().split('T')[0] +
        '-' +
        Math.round(date.getTime() / 1000);

    // make and click a temporary link to download the Blob
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `converted-images-${formattedDate}.zip`;
    link.click();
    link.remove();

    amplitude.track(`Zip download successfull`);

    showFeedbackToast();
};

const onWindowMouseDown = (e: MouseEvent) => {
    mouseDown.value = true;
};

const onWindowMouseUp = (e: MouseEvent) => {
    mouseDown.value = false;
};

const onWindowMouseMove = (e: MouseEvent) => {};

const onWindowKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        // close content app container
        chrome.runtime.sendMessage({
            message: 'close_content_app',
        });
    }
};

const onWindowDragEnter = (e: DragEvent) => {
    isFullscreenDropZoneVisible.value = true;
};

const onFullscreenDropzoneDragLeave = (e: DragEvent) => {
    isFullscreenDropZoneVisible.value = false;
};

const onWindowMessage = (e: MessageEvent) => {
    const msg = e.data as ConvertHeicResultMessage;
    if (msg.message == 'convert_heic_result') {
        const index = files.items.findIndex((item) => item.id == msg.itemId);
        if (index >= 0) {
            files.items[index].processing = false;
            files.items[index].format = msg.format;
            if (msg.error) {
                if (isDevelopment()) {
                    console.log('HEIC convert error:', msg.errorObject);
                }
                // is this another format?
                const formats = [
                    'image/png',
                    'image/jpeg',
                    'image/gif',
                    'image/webp',
                ];
                for (let i = 0; i < formats.length; i++) {
                    if (msg.errorObject?.message.includes(formats[i])) {
                        files.items[index].error = t(
                            'unsupported_format_error'
                        );
                        files.items[index].detectedFormat = formats[i]
                            .replace('image/', '')
                            .replace('jpeg', 'jpg');
                        // in this case, this is the original file blob
                        files.items[index].src = msg.url;
                        return;
                    }
                }

                files.items[index].error = t('invalid_image_error');
            } else {
                files.items[index].src = msg.url;
            }
        }
        if (isDevelopment()) {
            console.log('[DEV] Conversion results', files.items);
        }
    }

    // paywall data
    const isFromPayments = [
        'https://onlineapp.pro',
        'https://onlineapp.live',
        'https://onlineapp.stream',
    ].includes(e.origin);

    if (isFromPayments) {
        if (e.data?.type === 'remove') {
            if (isDevelopment()) {
                console.log('[DEV] paywall type == remove', e.data);
            }
        }

        if (e.data?.type === 'state') {
            const state = e.data.state as PaywallState;
            if (isDevelopment()) {
                console.log('[DEV] paywall state', state);
            }
            // signed status
            if (state?.auth_status === 'signed-out') {
                return;
            }
            if (state?.auth_status === 'signed-in') {
                // an attempt to receive user profile data here,
                // additionally to the getUser() method
                const userData = state.user as PaywallUserProfile;
                if (!user.record) {
                    user.record = {} as PaywallUser;
                }
                user.record.user = userData;
                return;
            }
            // popup visibility
            if (e.data?.state?.visibility_status === 'visible') {
                if (isDevelopment()) {
                    console.log('[DEV] paywall popup is visible');
                }
            } else {
                if (isDevelopment()) {
                    console.log('[DEV] paywall popup is hidden');
                }
            }
            // payments state
            if (e.data?.state?.purchase_status === 'paid') {
                // User has an active subscription or just paid
                if (isDevelopment()) {
                    console.log('[DEV] paywall purchase status: paid');
                }
            } else if (e.data?.state?.purchase_status !== 'init') {
                // The user either did not pay, or the subscription has expired,
                // or simply left after seeing the paywall
                if (isDevelopment()) {
                    console.log(
                        '[DEV] paywall purchase status: not paid or expired'
                    );
                }
            } else if (
                e.data?.state?.visibility_status_reason === 'time-trial'
            ) {
                // The user is in the trial period
                if (isDevelopment()) {
                    console.log('[DEV] paywall purchase status: trial period');
                }
            }
        }
    }
};

/**
 * If there is no install data, user is considered old
 * @return {Promise<boolean>}
 */
const isNewUser = async () => {
    if (!isInExtension()) {
        return true;
    }
    const { installDate } = await chrome.storage.sync.get({
        installDate: null,
    });
    if (installDate) {
        return true;
    }
    return false;
};

// Monetization library works only for new users
// and for non-extension environment
const shouldConsiderPayments = async () => {
    const newUser = await isNewUser();
    return !isInExtension() || newUser;
};

const getUser = async () => {
    userInfoReady.value = false;
    // NOTE: it looks like this may not be resolved if signed out
    // Duplicating user data in onWindowMessage.
    user.record = await getPaywallUser();
    userInfoReady.value = true;

    if (user.record?.user?.id) {
        // Identify user
        Bugsnag.setUser(user.record.user.id);
    }

    // H.identify(user.record.user.id, {
    //     paid: user.record.paid,
    // });
};

const onSignedIn = () => {
    if (isDevelopment()) {
        console.log('[dev] onSignedIn() called');
    }
    getUser();
};

const onStartedSignedOut = () => {
    amplitude.track(`Paywall: sign out`);
    if (isDevelopment()) {
        console.log('[dev] onStartedSignedOut() called');
    }
    // userInfoReady.value = false;
    if (user.record) {
        user.record.user = undefined;
    }
};

const onSignedOut = async () => {
    if (isDevelopment()) {
        console.log('[dev] onSignedOut() called');
    }
    await getUser();
    // init flowbite tooltips
    await nextTick();
    initTooltips();
};

const onAccountClick = () => {
    amplitude.track(`Go to account page`);
};

const onValidPromocode = () => {
    hasValidPromocode.value = true;
};

const onFeedback = (feedback: FeedbackType) => {
    let url = '';
    switch (feedback) {
        case 'bad':
        case 'could-be-better':
            url = getFeedbackFormUrl();
            break;
        case 'good':
            url = getExtensionReviewsUrl();
            break;
    }
    if (!url) {
        return;
    }
    feedbackToastElm.value?.close();
    if (isInExtension()) {
        chrome.tabs.create({ url: url });
        return;
    }
    window.open(url, '_blank');
};

const showFeedbackToast = () => {
    feedbackToastElm.value?.open();
};

const onLangUpdate = (newLang: string) => {
    amplitude.track(`Change language to ${newLang}`);
    // update global var
    lang.value = newLang as LanguageCode;

    saveToStorage('lang', newLang);
};

const onHowToClick = () => {
    amplitude.track(`How-To button clicked`);
    openHelpPage();
};

/**
 * @return {Promise<boolean>} non-false if payment is required, false otherwise
 */
const openPaywall = async () => {
    const rs = await shouldConsiderPayments();

    if (!rs) {
        // User is not new, do not open the paywall
        amplitude.track(`Paywall: old user, skip`);
        return false;
    }

    // Before checking paywall, check if there is any valid promo code
    try {
        const promocode = await getPromocode();
        if (promocode) {
            const valid = await validatePromocode(
                promocode,
                user.record?.user?.email
            );
            if (valid) {
                hasValidPromocode.value = true;
                amplitude.track(`Paywall: has valid promo code, skip`);
                return false;
            }

            // it's not valid, delete
            setPromocode('');
        }
    } catch (error) {
        //
    }

    // Monetization library
    // window.paywall.open();
    // return true;

    try {
        amplitude.track(`Open paywall`);
        // const paywallRs = await window.paywall.open();
        const paywallRs: PaywallState = await window.paywall.open({
            // Manage when the promise will be resolved:
            //   'success-purchase' -- when user paid or restore purchase
            //   'opened' -- when the paywall opens
            //   'signed-in' -- when user signed in
            resolveEvent: 'success-purchase',
        });
        if (isDevelopment()) {
            console.log('[DEV] paywallRs', paywallRs);
        }
        if (
            typeof paywallRs === 'object' &&
            'purchase_status' in paywallRs &&
            paywallRs?.purchase_status === 'paid'
        ) {
            amplitude.track(`Paywall: paid`);
            return false;
        }
        // The user is in the trial period
        if (
            typeof paywallRs === 'object' &&
            'visibility_status_reason' in paywallRs &&
            paywallRs?.visibility_status_reason === 'time-trial'
        ) {
            amplitude.track(`Paywall: trial period`);
            return false;
        }
        return true;
    } catch (error) {
        if (isDevelopment()) {
            console.log('[DEV] openPaywall() error', error);
        }
        // This is a legit paywall response
        if (typeof error === 'object' && error !== null) {
            // Paid
            if (
                'purchase_status' in error &&
                error.purchase_status === 'paid'
            ) {
                amplitude.track(`Paywall: paid (error branch)`);
                return false;
            }

            // There may be multiple reasons for invisible paywall
            // but they all seem to be legit - do not block the user in these cases
            // Examples: trial, country-not-match, etc.
            // UPD: except for "closed-by-user" reason
            if (
                'visibility_status' in error &&
                error.visibility_status === 'invisible' &&
                'visibility_status_reason' in error &&
                error.visibility_status_reason !== 'closed-by-user'
            ) {
                amplitude.track(`Paywall: invisible (error branch)`);
                return false;
            }

            // Trial period
            // if (
            //     'visibility_status_reason' in error &&
            //     'visibility_status' in error &&
            //     error.visibility_status === 'invisible' &&
            //     error.visibility_status_reason === 'openings-trial'
            // ) {
            //     return false;
            // }

            // not paid
            amplitude.track(`Paywall: not paid (error branch)`);
            return true;
        }

        Bugsnag.notify(error as NotifiableError);
        // Mark as non-paid as it seems there is an error
        // and it should be fixed
        amplitude.track(`Paywall: error`);
        return false;
    }
};

onMounted(async () => {
    amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
        autocapture: false,
    });

    amplitude.track(`Main window opened`);

    /** @ts-ignore */
    // paywallUserData = await window.paywall.getUser();
    // console.log('paywallUserData', paywallUserData);
    // Monetization library
    // @ts-ignore
    // window.paywall.open();

    // ONLY FOR NEW USERS:
    // const date = await chrome.storage.sync.get('installDate');
    // if (date) {
    //     // @ts-ignore
    //     window.paywall.open();
    // } else {
    //     // User is not new, do not open the paywall
    // }

    let langDetected = await detectLangOnPageLoad();

    if (langDetected) {
        if (isDevelopment()) {
            console.log('[DEV] lang detected', langDetected);
        }
        lang.value = langDetected as LanguageCode;
        setI18nLanguage(langDetected);
    }

    if (window.location.search.includes('tab=1')) {
        isOpenedInTab.value = true;
    }

    // init event handlers
    document.addEventListener('mousemove', onWindowMouseMove, false);
    document.addEventListener('mouseup', onWindowMouseUp, false);
    document.addEventListener('mousedown', onWindowMouseDown, false);
    document.addEventListener('keydown', onWindowKeyDown, false);
    document.addEventListener('dragenter', onWindowDragEnter, false);
    window.addEventListener('message', onWindowMessage, false);
    // init modal
    modal = new Modal(modalElm.value, modalOptions, instanceOptions);

    // TESTS
    // files.items.push({
    //     id: uniqueId(),
    //     name: 'test.heic',
    //     shortName: getShortFileName('test.heic'),
    //     src: '',
    //     processing: true,
    //     error: 'Error',
    //     blob: null,
    // });
    // files.items.push({
    //     id: uniqueId(),
    //     name: 'test.heic',
    //     shortName: getShortFileName('test.heic'),
    //     src: '',
    //     processing: false,
    //     error: 'Could not convert',
    //     blob: null,
    // });

    if (await shouldConsiderPayments()) {
        getUser();
    }

    // init flowbite tooltips
    await nextTick();
    initTooltips();
});

onUnmounted(() => {
    document.removeEventListener('mousemove', onWindowMouseMove, false);
    document.removeEventListener('mouseup', onWindowMouseUp, false);
    document.removeEventListener('mousedown', onWindowMouseDown, false);
    document.removeEventListener('keydown', onWindowKeyDown, false);
    document.removeEventListener('dragenter', onWindowDragEnter, false);
    window.removeEventListener('message', onWindowMessage, false);
});
</script>

<style lang="scss" scoped>
.item-icon {
    box-shadow: 0 0 4px 1px rgba(57, 76, 96, 0.15),
        0 0 0 1px rgba(43, 59, 74, 0.3);
    transition: color 0.1s linear, background-color 0.1s linear;
}
</style>
